import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import type {
  SubscriptionPlan,
  SubscriptionCreateRequest,
} from '../../types/api/subscription';
import { formatDuration, formatTier, formatPrice } from '../../types/api/subscription';

export interface SubscriptionAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SubscriptionCreateRequest) => void | Promise<void>;
  userId?: string; // Pre-selected user ID
  userName?: string; // Pre-selected user name for display
  subscriptionPlans: SubscriptionPlan[]; // Available plans
}

type AssignmentFormState = {
  userId: string;
  planId: string;
  startDate: string;
  notes: string;
};

const createDefaultState = (): AssignmentFormState => ({
  userId: '',
  planId: '',
  startDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
  notes: '',
});

export const SubscriptionAssignmentModal: React.FC<SubscriptionAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userId,
  userName,
  subscriptionPlans,
}) => {
  const [formData, setFormData] = useState<AssignmentFormState>(createDefaultState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  // Pre-fill user ID if provided
  useEffect(() => {
    if (isOpen) {
      const defaultState = createDefaultState();
      if (userId) {
        defaultState.userId = userId;
      }
      setFormData(defaultState);
      setErrors({});
      setSelectedPlan(null);
    }
  }, [userId, isOpen]);

  // Update selected plan when planId changes
  useEffect(() => {
    if (formData.planId) {
      const plan = subscriptionPlans.find((p) => p.planId === formData.planId);
      setSelectedPlan(plan || null);
    } else {
      setSelectedPlan(null);
    }
  }, [formData.planId, subscriptionPlans]);

  const handleChange = (field: keyof AssignmentFormState, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userId.trim()) {
      newErrors.userId = 'User ID is required';
    }

    if (!formData.planId.trim()) {
      newErrors.planId = 'Please select a subscription plan';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format date as LocalDateTime (yyyy-MM-dd'T'HH:mm:ss) without timezone
      let formattedStartDate: string | undefined = undefined;
      if (formData.startDate) {
        const date = new Date(formData.startDate);
        // Format: yyyy-MM-ddTHH:mm:ss (no timezone, no milliseconds)
        formattedStartDate = date.getFullYear() + '-' +
          String(date.getMonth() + 1).padStart(2, '0') + '-' +
          String(date.getDate()).padStart(2, '0') + 'T' +
          String(date.getHours()).padStart(2, '0') + ':' +
          String(date.getMinutes()).padStart(2, '0') + ':' +
          String(date.getSeconds()).padStart(2, '0');
      }

      const payload: SubscriptionCreateRequest = {
        userId: formData.userId,
        planId: formData.planId,
        startDate: formattedStartDate,
        notes: formData.notes || undefined,
      };

      await onSubmit(payload);
      handleClose();
    } catch (error) {
      console.error('Subscription assignment error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(createDefaultState());
      setErrors({});
      setSelectedPlan(null);
      onClose();
    }
  };

  // Calculate end date based on start date and selected plan duration (in months).
  const calculateEndDate = (): string | null => {
    if (!formData.startDate || !selectedPlan) return null;

    const months = selectedPlan.durationMonths;
    if (!Number.isFinite(months) || months <= 0) return null;

    const startDate = new Date(formData.startDate);
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + months);

    return endDate.toISOString().split('T')[0];
  };

  const activePlans = subscriptionPlans.filter((p) => p.isActive !== false);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Assign Subscription to User" size="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* User Information */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            User <span className="text-red-500">*</span>
          </label>
          {userName ? (
            <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
              {userName}
            </div>
          ) : (
            <>
              <input
                type="text"
                value={formData.userId}
                onChange={(e) => handleChange('userId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                placeholder="Enter User ID"
              />
              {errors.userId && <p className="text-red-500 text-xs mt-1">{errors.userId}</p>}
            </>
          )}
        </div>

        {/* Subscription Plan Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subscription Plan <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.planId}
            onChange={(e) => handleChange('planId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
          >
            <option value="">Select a plan...</option>
            {activePlans.map((plan) => (
              <option key={plan.planId} value={plan.planId}>
                {plan.name} - {formatDuration(plan.durationMonths)} - {formatPrice(plan.finalPrice, plan.currency)}
              </option>
            ))}
          </select>
          {errors.planId && <p className="text-red-500 text-xs mt-1">{errors.planId}</p>}
        </div>

        {/* Selected Plan Details */}
        {selectedPlan && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold text-blue-900">{selectedPlan.name}</h4>
            {selectedPlan.description && <p className="text-sm text-blue-800">{selectedPlan.description}</p>}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-medium text-blue-900">Duration:</span>{' '}
                <span className="text-blue-800">{formatDuration(selectedPlan.durationMonths)}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Tier:</span>{' '}
                <span className="text-blue-800">{formatTier(selectedPlan.tierGranted)}</span>
              </div>
              <div>
                <span className="font-medium text-blue-900">Price:</span>{' '}
                <span className="text-blue-800">
                  {formatPrice(selectedPlan.finalPrice, selectedPlan.currency)}
                  {selectedPlan.discountPercentage && selectedPlan.discountPercentage > 0 && (
                    <span className="ml-1 line-through text-gray-600 text-xs">
                      {formatPrice(selectedPlan.price, selectedPlan.currency)}
                    </span>
                  )}
                </span>
              </div>
            </div>
            <div className="pt-2 border-t border-blue-300">
              <p className="text-xs font-medium text-blue-900 mb-1">Features:</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-blue-800">
                {selectedPlan.features.downloadEnabled && <span>✓ Downloads</span>}
                {selectedPlan.features.hdEnabled && <span>✓ HD Quality</span>}
                {selectedPlan.features.uhdEnabled && <span>✓ UHD/4K Quality</span>}
                {selectedPlan.features.offlineViewing && <span>✓ Offline Viewing</span>}
                {!selectedPlan.features.adsEnabled && <span>✓ Ad-Free</span>}
              </div>
            </div>
          </div>
        )}

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
          />
          {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
        </div>

        {/* End Date Display (calculated) */}
        {calculateEndDate() && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Calculated)</label>
            <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">{calculateEndDate()}</div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes (optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            placeholder="e.g., Promotional subscription grant, special offer, etc."
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" onClick={handleClose} disabled={isSubmitting} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !selectedPlan}>
            {isSubmitting ? 'Assigning...' : 'Assign Subscription'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

